import {
  ClassicEditor,
  InlineEditor,
  Alignment,
  AccessibilityHelp,
  Autoformat,
  AutoImage,
  Autosave,
  BlockQuote,
  Bold,
  CloudServices,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Paragraph,
  SelectAll,
  SimpleUploadAdapter,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo,
  Font,
  HtmlEmbed,
} from "ckeditor5";

// Image uploads post to this endpoint, which stores the file via the app's S3
// flow (falling back to local disk in dev). Shared by every editor config.
const IMAGE_UPLOAD_URL = "/admin/inline-upload/image";

function simpleUploadConfig() {
  const csrfToken =
    document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ||
    "";

  return {
    uploadUrl: IMAGE_UPLOAD_URL,
    headers: { "X-CSRF-Token": csrfToken },
  };
}

// Image plugins shared between the full and email editor configs.
const imagePlugins = [
  AutoImage,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  LinkImage,
  SimpleUploadAdapter,
];

const imageToolbar = [
  "toggleImageCaption",
  "imageTextAlternative",
  "|",
  "imageStyle:inline",
  "imageStyle:wrapText",
  "imageStyle:breakText",
  "|",
  "resizeImage",
];

// import 'ckeditor5/ckeditor5.css';

export const editorConfig = {
  plugins: [
    Alignment,
    AccessibilityHelp,
    Autoformat,
    AutoImage,
    Autosave,
    BlockQuote,
    Bold,
    CloudServices,
    Essentials,
    Heading,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    Paragraph,
    SelectAll,
    SimpleUploadAdapter,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline,
    Undo,
    Font,
    HtmlEmbed,
  ],
  simpleUpload: simpleUploadConfig(),
  heading: {
    options: [
      {
        model: "paragraph",
        title: "Paragraph",
        class: "ck-heading_paragraph",
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
      {
        model: "heading5",
        view: "h5",
        title: "Heading 5",
        class: "ck-heading_heading5",
      },
      {
        model: "heading6",
        view: "h6",
        title: "Heading 6",
        class: "ck-heading_heading6",
      },
    ],
  },
  image: {
    toolbar: [
      "toggleImageCaption",
      "imageTextAlternative",
      "|",
      "imageStyle:inline",
      "imageStyle:wrapText",
      "imageStyle:breakText",
      "|",
      "resizeImage",
    ],
  },
  initialData: "",
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
    decorators: {
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: {
          download: "file",
        },
      },
    },
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  fontSize: {
    // options: [
    //     'tiny',
    //     'small',
    //     'default',
    //     'medium',
    //     'big',
    //     'huge'
    // ]
    options: [
      {
        model: "Tiny",
        title: "Tiny",
        view: { name: "span", classes: ["text-xxs", "md:text-xs"] },
      },
      {
        model: "Small",
        title: "Small",
        view: { name: "span", classes: ["text-sm", "md:text-md"] },
      },
      {
        model: "Default",
        title: "Default",
        view: { name: "span", classes: ["text-base", "md:text-lg"] },
      },
      {
        model: "Medium",
        title: "Medium",
        view: { name: "span", classes: ["text-lg", "md:text-2xl"] },
      },
      {
        model: "Big",
        title: "Big",
        view: { name: "span", classes: ["text-2xl", "md:text-3xl"] },
      },
      {
        model: "Large",
        title: "Large",
        view: { name: "span", classes: ["text-3xl", "md:text-4xl"] },
      },
      {
        model: "Huge",
        title: "Huge",
        view: { name: "span", classes: ["text-4xl", "md:text-6xl"] },
      },
    ],
  },
  placeholder: "Type or paste your content here!",
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties",
    ],
  },
  toolbar: {
    items: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "alignment",
      "|",
      "link",
      "insertImage",
      "|",
      "bulletedList",
      "numberedList",
      "todoList",
      "insertTable",
      "blockQuote",
      "|",
      "outdent",
      "indent",
      "htmlEmbed",
    ],
    shouldNotGroupWhenFull: false,
  },
};

const emailEditorConfig = {
  ...editorConfig,
  plugins: [
    AccessibilityHelp,
    Autoformat,
    Autosave,
    BlockQuote,
    Bold,
    Essentials,
    Heading,
    Italic,
    Link,
    List,
    ListProperties,
    Paragraph,
    SelectAll,
    TextTransformation,
    Underline,
    Undo,
    Font,
    ...imagePlugins,
  ],
  fontSize: {
    options: [12, 14, "default", 16, 18, 24, 32],
    supportAllValues: false,
  },
  image: {
    toolbar: imageToolbar,
  },
  simpleUpload: simpleUploadConfig(),
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
  },
  toolbar: {
    items: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "link",
      "insertImage",
      "|",
      "bulletedList",
      "numberedList",
      "blockQuote",
    ],
    shouldNotGroupWhenFull: false,
  },
};

function editorConfigFor(node) {
  if (node?.dataset?.editorConfig === "email") {
    return emailEditorConfig;
  }

  return editorConfig;
}

export function createInlineEditor(node, data = "") {
  return InlineEditor.create(node, { ...editorConfigFor(node), initialData: data });
}

const editorRegistry = new Map();

function registerEditor(editor, ...ids) {
  ids.filter(Boolean).forEach((id) => editorRegistry.set(id, editor));
}

function protectEditorForm(editor, node) {
  const form = node?.closest("form");

  if (!form) {
    return;
  }

  let ckEditorInteraction = false;
  let interactionTimer = null;

  const markRecentEditorInteraction = () => {
    ckEditorInteraction = true;
    window.clearTimeout(interactionTimer);
    interactionTimer = window.setTimeout(() => {
      ckEditorInteraction = false;
    }, 10000);
  };

  const editorChrome = () => {
    const editable = editor?.ui?.getEditableElement?.();
    return editable?.closest(".ck-editor") || node.closest(".ck-editor") || form;
  };

  const markEditorButtons = () => {
    editorChrome()
      ?.querySelectorAll(".ck button:not([type]), .ck button[type='submit']")
      .forEach((button) => {
        button.type = "button";
      });
  };

  const trackEditorInteraction = (event) => {
    if (!event.target?.closest?.(".ck")) {
      return;
    }

    markRecentEditorInteraction();
  };

  const stopNativeSubmit = (event) => {
    const ckSubmitter = event.submitter?.closest?.(".ck");
    const noExplicitSubmitter = !event.submitter;

    if (ckSubmitter || (ckEditorInteraction && noExplicitSubmitter)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  markEditorButtons();

  const observer = new MutationObserver(markEditorButtons);
  observer.observe(form, { childList: true, subtree: true });

  form.addEventListener("pointerdown", trackEditorInteraction, true);
  form.addEventListener("click", trackEditorInteraction, true);
  form.addEventListener("change", trackEditorInteraction, true);
  form.addEventListener("submit", stopNativeSubmit, true);

  editor.on("destroy", () => {
    observer.disconnect();
    window.clearTimeout(interactionTimer);
    form.removeEventListener("pointerdown", trackEditorInteraction, true);
    form.removeEventListener("click", trackEditorInteraction, true);
    form.removeEventListener("change", trackEditorInteraction, true);
    form.removeEventListener("submit", stopNativeSubmit, true);
  });
}

function syncInlineInput(inlineId, editor) {
  const input = document.getElementById(inlineId);
  const data = editor.getData();

  if (!input) {
    syncEditorShadows(inlineId, data);
    return;
  }

  input.value = data;
  syncEditorShadows(inlineId, data);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

function syncEditorShadows(inlineId, data) {
  document
    .querySelectorAll(`[data-editor-shadow-for="${CSS.escape(inlineId)}"]`)
    .forEach((shadow) => {
      shadow.value = data;
    });
}

function syncSubjectShadows(subjectInputId, value) {
  document
    .querySelectorAll(`[data-subject-shadow-for="${CSS.escape(subjectInputId)}"]`)
    .forEach((shadow) => {
      shadow.value = value;
    });
}

function insertLink(targetId, url, defaultText = "") {
  const editor = editorRegistry.get(targetId);

  if (!editor || !url) {
    return;
  }

  const text = window.prompt("Link text", defaultText || url);

  if (!text || text.trim() === "") {
    return;
  }

  editor.model.change((writer) => {
    const linkedText = writer.createText(text.trim(), { linkHref: url });
    editor.model.insertContent(linkedText, editor.model.document.selection);
  });

  syncInlineInput(targetId, editor);
  editor.editing.view.focus();
}

function insertHtml(targetId, html) {
  const editor = editorRegistry.get(targetId);

  if (!editor || !html) {
    return;
  }

  const viewFragment = editor.data.processor.toView(html);
  const modelFragment = editor.data.toModel(viewFragment);

  editor.model.insertContent(modelFragment, editor.model.document.selection);
  syncInlineInput(targetId, editor);
  editor.editing.view.focus();
}

function applyEmailTemplate(targetId, subjectInputId, subject, body) {
  const editor = editorRegistry.get(targetId);
  const subjectInput = document.getElementById(subjectInputId);

  if (subjectInput && subject != null && subject !== "") {
    subjectInput.value = subject;
    syncSubjectShadows(subjectInputId, subject);
    subjectInput.dispatchEvent(new Event("input", { bubbles: true }));
  }

  if (!editor) {
    return;
  }

  if (body != null && body !== "") {
    editor.setData(body);
    syncInlineInput(targetId, editor);
    editor.editing.view.focus();
  }
}

window.RichTextEditor = window.RichTextEditor || {};
window.RichTextEditor.insertLink = insertLink;
window.RichTextEditor.insertHtml = insertHtml;
window.RichTextEditor.applyEmailTemplate = applyEmailTemplate;

// let markdownEditor;
function openNote(data, convert, inline, node, changeCallback) {
  requestAnimationFrame(() => {
    let initData;
    if (convert) {
      initData = "";
    } else {
      initData = data;
    }

    let newConfig = { ...editorConfigFor(node), initialData: initData };

    if (node && inline != null && inline != "") {
      InlineEditor.create(node, newConfig)
        .then((newEditor) => {
          // markdownEditor = newEditor;
          registerEditor(newEditor, node.id, inline);
          protectEditorForm(newEditor, node);
          newEditor.model.document.on("change:data", () => {
            syncInlineInput(inline, newEditor);
          });
          return newEditor;
        })
        .catch((error) => console.error(error));
    } else if (node) {
      ClassicEditor.create(node, newConfig)
        .then((newEditor) => {
          // markdownEditor = newEditor;
          registerEditor(newEditor, node.id);
          protectEditorForm(newEditor, node);
          //  markdownEditor.model.document.on('change:data', () => {
          //      node.value = markdownEditor.getData();
          //  } );
          return newEditor;
        })
        .catch((error) => console.error(error));
    }
  });
}

function updateContent(fileNote) {
  // markdownEditor.setData(fileNote.content);
}

function saveNote(editor, changeCallback) {
  const editorData = editor.getData();
  editor = null;

  changeCallback(editorData);
  return editorData;
}

function destroy() {
  // fileNoteEditor.destroy().catch((error) => console.log(error));
}

export default {
  openNote,
  updateContent,
  destroy,
};
